import React, { useState, useEffect, useMemo, useRef } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { MdOutlineFileUpload } from "react-icons/md";
import redirectURL from "./redirectURL";
import * as XLSX from "xlsx";
import "../assets/css/inventorymanagement.css";

const SkuCategorySequence = () => {
    const [loadshow, setLoadshow] = useState("show-n");
    const [overlayBlock, setOverlayBlock] = useState("show-n");
    const [overlyBlock, setOverlyBlock] = useState("show-n");
    const [show, setShow] = useState(false);
    const [basicTitle, setBasicTitle] = useState("");
    const [basicType, setBasicType] = useState("default");
    const [rowData, setRowData] = useState([]);
    const [columnWithDefs, setColumnWithDefs] = useState([]);
    
    // Upload-related states
    const [isSliderOpen, setIsSliderOpen] = useState(false);
    const [sliderType, setSliderType] = useState(null);
    const [sliderCl, setSliderCl] = useState("slider-25");
    const [sliderTitle, setSliderTitle] = useState("");
    const [uploadedFileData, setUploadedFileData] = useState([]);
    const [filename, setFilename] = useState("");
    const [file, setFile] = useState(null);
    const [showDownloadMessage, setShowDownloadMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorData, setErrorData] = useState([]);

    const gridColumnApi = useRef(null);
    const gridApi = useRef(null);

    const autoSizeAllColumns = () => {
        if (gridColumnApi.current) {
            const allColumns = gridColumnApi.current.getAllColumns();
            if (allColumns?.length) {
                const allColumnIds = allColumns.map((col) => col.getColId());
                gridColumnApi.current.autoSizeColumns(allColumnIds, false);
            }
        }
    };

    const onGridReady = (params) => {
        gridApi.current = params.api;
        gridColumnApi.current = params.columnApi;
        setTimeout(() => autoSizeAllColumns(), 100);
    };

    // Column definitions for SKU Category Sequence
    const skuCategoryCols = useMemo(() => [
        { 
            headerName: "Category", 
            field: "category", 
            width: 250,
            sortable: true,
            filter: true,
        },
        { 
            headerName: "SKU", 
            field: "sku", 
            width: 150,
            sortable: true,
            filter: true,
        },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
    }), []);

    const sideBar = useMemo(() => ({
        toolPanels: [
            { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
            { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" },
        ],
    }), []);

    const statusPanel = useMemo(() => ({
        statusPanels: [
            { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
            { statusPanel: "agTotalRowCountComponent", align: "center" },
            { statusPanel: "agFilteredRowCountComponent" },
            { statusPanel: "agSelectedRowCountComponent" },
            { statusPanel: "agAggregationComponent" },
        ],
    }), []);

    const getSkuCategoryData = async () => {
        try {
            const response = await redirectURL.post("masters/getSkuCategorySequence", {});
            const data = response?.data?.data || [];
            setRowData(Array.isArray(data) ? data : []);
            setColumnWithDefs(skuCategoryCols);
        } catch (err) {
            console.error("Error fetching SKU category sequence data:", err);
            setRowData([]);
            setColumnWithDefs(skuCategoryCols);
            setShow(true);
            setBasicType("error");
            setBasicTitle("Failed to fetch SKU category sequence data");
        }
    };

    const getMainData = async () => {
        setLoadshow("show-m");
        setOverlayBlock("show-m");
        await getSkuCategoryData();
        setLoadshow("show-n");
        setOverlayBlock("show-n");
    };

    useEffect(() => {
        getMainData();
    }, []);

    const closeAlert = () => {
        setShow(false);
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setOverlyBlock("show-n");
        setIsSliderOpen(false);
        setUploadedFileData([]);
        setShowDownloadMessage(false);
        setErrorMessage("");
        setErrorData([]);
        
        // Reset file input
        if (document.getElementById("skuCategoryUploadFile")) {
            document.getElementById("skuCategoryUploadFile").value = "";
        }
    };

    // Upload functions
    const onClickUploadSkuCategory = () => {
        setIsSliderOpen(true);
        setSliderType(1);
        setSliderCl("slider-25");
        setSliderTitle("Upload SKU Sequence Data");
        setOverlyBlock("show-m");
    };

    const onClickingSliderCloseIcon = () => {
        setOverlyBlock("show-n");
        setIsSliderOpen(false);
        if (document.getElementById("skuCategoryUploadFile")) {
            document.getElementById("skuCategoryUploadFile").value = "";
        }
        setUploadedFileData([]);
    };

    const hideOverlySlideBlock = () => {
        setOverlyBlock("show-n");
        setLoadshow("show-n");
        setIsSliderOpen(false);
        if (document.getElementById("skuCategoryUploadFile")) {
            document.getElementById("skuCategoryUploadFile").value = "";
        }
        setUploadedFileData([]);
    };

    const downloadError = () => {
        if (!errorData || errorData.length === 0) {
            alert("No error data available");
            return;
        }

        let dataToProcess = errorData;
        if (!Array.isArray(errorData)) {
            if (errorData.data && Array.isArray(errorData.data)) {
                dataToProcess = errorData.data;
            } else if (typeof errorData === 'object') {
                dataToProcess = [errorData];
            } else {
                alert("Error data format not supported");
                return;
            }
        }

        if (dataToProcess.length === 0) {
            alert("No error data to download");
            return;
        }

        const allKeys = [...new Set(dataToProcess.flatMap(item => Object.keys(item)))];

        const data = dataToProcess.map(item => {
            const row = {};
            allKeys.forEach(key => {
                row[key] = item[key] || "";
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Error_Data");
        XLSX.writeFile(workbook, `sku_category_error_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFile(e.target.value);
        setFilename(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            if (!worksheet) {
                console.error("Sheet not found!");
                setShow(true);
                setBasicType("info");
                setBasicTitle("Invalid file format");
                return;
            }

            const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
            
            // Convert headers to lowercase
            const convertedData = jsonData.map(row => {
                const newRow = {};
                Object.keys(row).forEach(key => {
                    const lowercaseKey = key.toLowerCase().replace(/\s+/g, '_');
                    newRow[lowercaseKey] = row[key];
                });
                return newRow;
            });
            
            console.log("Parsed SKU Category Data:", convertedData);
            setUploadedFileData(convertedData);
        };

        reader.readAsArrayBuffer(file);
    };
  const triggerUserAction = (trigerdata) => {
        redirectURL.post('inventory/triggerUserAction', trigerdata)
    }
    const handleSkuCategoryUpload = async () => {
        if (uploadedFileData.length === 0) {
            setShow(true);
            setBasicType("info");
            setBasicTitle("Please upload a file");
            return;
        }

        try {
            setLoadshow("show-m");
            setOverlayBlock("show-m");
            
            const params = {
                data: uploadedFileData
            };
            
            const response = await redirectURL.post("masters/skusequence", params);
            
            setLoadshow("show-n");
            setOverlayBlock("show-n");

            const res = response?.data?.pyResponse || response?.data;

            const hasErrorData = res?.response_type && 
                                 res?.response_data && 
                                 res.response_data !== "" &&
                                 (Array.isArray(res.response_data) ? 
                                  res.response_data.length > 0 : true);
            
            const downloadableErrorTypes = [
                "SKU Code Not Found Error",
                "Category Missing Error",
                "Duplicate Entry Error",
                "Invalid Format Error"
            ];
            
            const isDownloadableError = downloadableErrorTypes.includes(res?.response_type);
            
            if (res?.status === "Success") {
                await getMainData();
                setShow(true);
                setBasicType("success");
                setBasicTitle(res.message || "SKU Category data uploaded successfully");
                onClickingSliderCloseIcon();
                  const userData = {
                    email: localStorage.getItem("email"),
                    user_code: localStorage.getItem("user_code"),
                    action: "Uploaded SKU Category Sequence data",
                    status: "Success",
                    path: window.location.pathname
                };
                triggerUserAction(userData);
            } else {
                setShow(true);
                setBasicType(isDownloadableError && hasErrorData ? "warning" : "info");
                setBasicTitle(res?.message || "Failed to upload SKU Category data");
                    const userData = {
                        email: localStorage.getItem("email"),
                        user_code: localStorage.getItem("user_code"),
                        action: "Failed to upload SKU Category Sequence data",
                        status: "Failed",
                        path: window.location.pathname
                    };
                    triggerUserAction(userData);

                if (isDownloadableError && hasErrorData) {
                    setShowDownloadMessage(true);
                    setErrorMessage(res?.message || "");
                    setErrorData(res?.response_data || []);
                } else {
                    setShowDownloadMessage(false);
                    setErrorMessage("");
                    setErrorData([]);
                }
            }
        } catch (error) {
            console.error("Upload SKU Category error:", error);
            const errorResponse = error.response?.data?.pyResponse || error.response?.data;
            
            if (errorResponse?.response_type && errorResponse?.response_data) {
                const downloadableErrorTypes = [
                    "SKU Code Not Found Error",
                    "Category Missing Error",
                    "Duplicate Entry Error",
                    "Invalid Format Error"
                ];
                
                const isDownloadableError = downloadableErrorTypes.includes(errorResponse.response_type);
                const hasErrorData = errorResponse.response_data !== "" &&
                                    (Array.isArray(errorResponse.response_data) ? 
                                     errorResponse.response_data.length > 0 : true);
                
                if (isDownloadableError && hasErrorData) {
                    setShowDownloadMessage(true);
                    setErrorMessage(errorResponse.message || "");
                    setErrorData(errorResponse.response_data);
                    setBasicType("warning");
                } else {
                    setBasicType("info");
                }
            } else {
                setBasicType("info");
            }
            
            const errorMessage = error.response?.data?.message || "Failed to upload SKU Category data";
            setShow(true);
            setBasicTitle(errorMessage);
            setLoadshow("show-n");
            setOverlayBlock("show-n");
        }
    };

    const downloadExcelTemplate = () => {
        // Create sample template data
        const sampleData = [
            { category: "Protein Bar", sku: "DBC" },
            { category: "Energy Drink", sku: "ED001" },
            { category: "Snacks", sku: "SNK001" }
        ];
        
        const worksheet = XLSX.utils.json_to_sheet(sampleData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "SKU_Sequence_Template");
        XLSX.writeFile(workbook, "sku_sequence_template.xlsx");
    };

    return (
        <>
            <div className="container-fluid mt-3">
                <div style={{ zIndex: "9999999" }}>
                    <SweetAlert
                        show={show}
                        type={basicType}
                        title={basicTitle}
                        onConfirm={closeAlert}
                        style={{ fontSize: "9px" }}
                    
                    >
                        {errorMessage && <p style={{ marginTop: "10px", fontSize: "12px" }}>{errorMessage}</p>}
                    </SweetAlert>
                </div>

                <div className="col-sm-12 text-right pr-0 mb-4" style={{ display: "flex", justifyContent: "flex-end" }}>
    <button
        onClick={onClickUploadSkuCategory}
        style={{ 
            backgroundColor: "#93385D", 
            color: "#fff", 
            border: "2px solid #93385D", 
            borderRadius: 5, 
            padding: "6px 15px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer"
        }}
    >
        <MdOutlineFileUpload style={{ fontSize: "18px" }} />
        {" Upload SKU Sequence"}
    </button>
</div>

                <div className="row" style={{ fontSize: "0.9rem", fontFamily: "Arial, sans-serif" }}>
                    <div className="col-sm-12 p-0 pl-2 mt-6">
                        <div className="ag-theme-alpine" style={{ height: 450, width: "100%" }}>
                            <AgGridReact
                                rowData={rowData}
                                columnDefs={columnWithDefs}
                                defaultColDef={defaultColDef}
                                domLayout="normal"
                                sideBar={sideBar}
                                statusBar={statusPanel}
                                pagination
                                paginationPageSize={100}
                                enableRangeSelection
                                onGridReady={onGridReady}
                                gridOptions={{ suppressHorizontalScroll: false }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Slider for Upload */}
            <div
                className={`stock-status-slider ${isSliderOpen ? "open" : ""} ${sliderCl}`}
                style={{ width: "25%" }}
            >
                <div className="slider-header-container d-flex justify-content-between">
                    <p className="m-0p">{sliderTitle}</p>
                    <p
                        className="m-0p"
                        style={{ cursor: "pointer" }}
                        onClick={onClickingSliderCloseIcon}
                    >
                        X&nbsp;&nbsp;&nbsp;
                    </p>
                </div>

                {sliderType === 1 && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                            overflowY: "auto",
                            padding: 5,
                        }}
                    >
                        <form 
                            style={{ flexGrow: 1, overflowY: "auto", padding: "10px" }}
                            onSubmit={(e) => { 
                                e.preventDefault(); 
                                handleSkuCategoryUpload(); 
                            }}
                        >
                            <div className="d-flex justify-content-end mb-10p">
                                <div>
                                    <button 
                                        type="button"
                                        id="sample-download-button" 
                                        onClick={downloadExcelTemplate}
                                        style={{
                                            padding: "8px 16px",
                                            backgroundColor: "#93385D",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                            fontSize: "13px"
                                        }}
                                    >
                                        Download Sample Template (.xlsx)
                                    </button>
                                </div>
                            </div>
                            
                            <div className="mb-1 mt-4" style={{ display: "flex", flexDirection: "column" }}>
                                <label style={{ fontSize: "13px", fontWeight: "400" }}>
                                    Upload File:
                                </label>
                                <input
                                    className="mt-3"
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileChange}
                                    style={{
                                        border: "1px solid lightgrey",
                                        width: "100%",
                                        padding: "8px",
                                        borderRadius: "5px",
                                        backgroundColor: "#f9f9f9",
                                        color: "#000",
                                        fontSize: "13px",
                                    }}
                                    id="skuCategoryUploadFile"
                                    required
                                />
                                <p style={{
                                    color: "red",
                                    fontSize: "13px",
                                    margin: "0px",
                                    marginTop: "10px",
                                }}>
                                    *Accept .csv & .xlsx files only
                                </p>
                            </div>

                            <div style={{ textAlign: "right", marginTop: "20px" }}>
                                <button
                                    className="btn btn-success"
                                    type="submit"
                                    style={{
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                        backgroundColor: "#93385D",
                                        border: "none",
                                        padding: "8px 20px",
                                        borderRadius: "5px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Upload
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>

            <div className={`loader ${loadshow}`} style={{ zIndex: "1002" }}></div>
            <div className={`overlay-block ${overlayBlock}`}></div>
            <div
                className={"overly-block " + overlyBlock}
                onClick={hideOverlySlideBlock}
            ></div>
        </>
    );
};

export default SkuCategorySequence;